import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import useDropSearchParam from 'hooks/useDropSearchParam';
import { useTour } from 'contexts/TourContext';
import { findMenuItem } from 'menu-items/catalog';
import { analytics } from 'lib/analytics';
import {
  PROMPTS,
  claimVisitSlot,
  isModalOpen,
  isVisitSlotFree,
  useOtherPromptShowing,
  useShowingPrompt,
  whenPageSettled
} from 'lib/prompts';
import { useCheckoutStore } from 'sections/account-settings/subscriptions/checkout-v2/store';
import { useQuickSubscribe } from 'sections/get-started/useQuickSubscribe';
import { ACTION_TARGETS, DEEP_LINK_PARAM, LEVELS, PANEL_SOURCES, PREVIEW_PARAM, SURFACES } from '../constants';
import { takeRememberedLink } from '../deepLink';
import { useLoadAnnouncements, useMarkSeen, useReadyRuleContext, useRuleContext, useWhatsNew } from '../hooks/useAnnouncements';
import { useLivePosts } from '../livePosts';
import { chipPosts, isBlockedRoute, onPath, pickPopup, visiblePosts } from '../rules';
import { useAnnouncementStore } from '../store';
import type { ActionTarget, CloseMethod, Post, RuleContext, TourPosition } from '../types';
import SpotlightDialog from './SpotlightDialog';
import PageHint from './PageHint';
import WhatsNewDrawer from './WhatsNewDrawer';

const byId = (posts: Post[], ids: string[]): Post[] => ids.flatMap((id) => posts.filter((post) => post.id === id));
const NO_PAGES: Post[] = [];

// Pages already counted, per popup. Outside the component: a layout remount mid-popup must not count them again.
const countedPages = new WeakMap<object, Set<string>>();

// A linked launch is the visit's popup; anything else opens as from the panel, so it starts no popup gap.
const linkedSurface = (post: Post) => (post.level === LEVELS.launch ? SURFACES.popup : SURFACES.panel);

/**
 * The launch popup, once a page has finished loading, at most once per visit and never over anything else on screen.
 * Mounted once in MainLayout; its state lives in the store, so layout remounts don't reset it.
 */
const AnnouncementHost = () => {
  useLoadAnnouncements();
  const { user } = useAuth();
  const userId = user?.tcId;
  const ctx = useRuleContext();
  const readyCtx = useReadyRuleContext();
  const livePosts = useLivePosts();
  const markSeen = useMarkSeen();
  const navigate = useNavigate();
  const dropParam = useDropSearchParam();
  const { pathname, search } = useLocation();
  const { active: tourActive } = useTour();
  const drawerOpen = useCheckoutStore((s) => s.drawerOpen);
  const quickSubscribeOpen = useQuickSubscribe((s) => s.open);
  const otherPromptShowing = useOtherPromptShowing(PROMPTS.announcement);
  const popup = useAnnouncementStore((s) => s.popup);
  const panelOpen = useAnnouncementStore((s) => s.panelOpen);
  const linkedPostId = useAnnouncementStore((s) => s.linkedPostId);
  const { open: openWhatsNew } = useWhatsNew();

  useShowingPrompt(PROMPTS.announcement, Boolean(popup) || panelOpen);

  // Nothing of ours or anyone else's on screen; the popup also needs a page it may open on.
  const screenFree = !popup && !panelOpen && !tourActive && !drawerOpen && !quickSubscribeOpen && !otherPromptShowing;
  const pageIsClear = screenFree && !new URLSearchParams(search).has(PREVIEW_PARAM) && !isBlockedRoute(pathname, search);

  // Each page moves the rules' clock, so scheduled posts appear and old ones expire without a reload.
  useEffect(() => useAnnouncementStore.getState().tick(), [pathname]);

  // The launches due on this page, if the popup may open here at all.
  const duePages = useMemo(
    () => (pageIsClear && ctx && userId && isVisitSlotFree(userId) ? pickPopup(livePosts, { ...ctx, now: Date.now() }) : NO_PAGES),
    [pageIsClear, ctx, userId, livePosts]
  );

  // It waits for the page to finish loading; any change here (a new page, the drawer opening) restarts or cancels that.
  useEffect(() => {
    if (!duePages.length || !userId) return undefined;
    return whenPageSettled(() => {
      // A dialog or menu opened meanwhile wins; the popup tries again on the next page.
      if (isModalOpen() || !claimVisitSlot(userId, PROMPTS.announcement)) return;
      useAnnouncementStore.getState().openPopup(
        duePages.map((post) => post.id),
        SURFACES.popup
      );
    });
  }, [duePages, userId, pathname, search]);

  // Opening an announced item's page clears its NEW chip; only once loaded, as chips only show then.
  useEffect(() => {
    if (!readyCtx) return;
    const here = chipPosts(livePosts, readyCtx).filter((post) => {
      const url = post.menuItemId ? findMenuItem(post.menuItemId)?.url : undefined;
      return Boolean(url) && onPath(pathname, url as string);
    });
    if (here.length)
      markSeen(
        here.map((post) => post.id),
        SURFACES.chip
      );
  }, [readyCtx, livePosts, pathname, markSeen]);

  // A launch email's link leaves the address bar at once, so no later page change loses it, and waits in the
  // store with any link kept through sign-in.
  const linkInUrl = new URLSearchParams(search).get(DEEP_LINK_PARAM);
  useEffect(() => {
    const remembered = takeRememberedLink();
    const id = linkInUrl ?? remembered;
    if (id) useAnnouncementStore.getState().setLinkedPostId(id);
    if (linkInUrl) dropParam(DEEP_LINK_PARAM);
  }, [linkInUrl, dropParam]);

  // It opens outside the popup rules (the person asked for it) once the state has loaded and the screen is free.
  useEffect(() => {
    if (!linkedPostId || !ctx || !userId || !screenFree) return;
    const live = (c: RuleContext) => visiblePosts(livePosts, { ...c, now: Date.now() }).find((p) => p.id === linkedPostId);
    const post = live(ctx);
    // A paid-only post waits for billing instead of falling back to the panel.
    if (!post && ctx.entitled === null && live({ ...ctx, entitled: true })) return;
    const store = useAnnouncementStore.getState();
    store.setLinkedPostId(null);
    if (!post) {
      openWhatsNew(PANEL_SOURCES.link);
      return;
    }
    // It is this visit's popup: nothing else pops up after it.
    claimVisitSlot(userId, PROMPTS.announcement);
    store.openPopup([post.id], linkedSurface(post));
  }, [linkedPostId, ctx, livePosts, userId, screenFree, openWhatsNew]);

  const posts = useMemo(() => (popup ? byId(livePosts, popup.postIds) : []), [popup, livePosts]);
  const surface = popup?.surface ?? SURFACES.popup;
  const { closePopup } = useAnnouncementStore.getState();

  const pageShown = (post: Post, at: TourPosition) => {
    markSeen([post.id], surface);
    if (!popup) return;
    // A post's later screens, or paging back to it, are not another showing.
    const counted = countedPages.get(popup) ?? new Set<string>();
    countedPages.set(popup, counted);
    if (counted.has(post.id)) return;
    counted.add(post.id);
    analytics.announcementShown({ post_id: post.id, level: post.level, surface, page: at.page + 1, pages: at.pages, steps: at.steps });
  };
  const act = (post: Post, target: ActionTarget, at: TourPosition) => {
    analytics.announcementClicked({ post_id: post.id, surface, target, page: at.page + 1, step: at.step + 1 });
    if (target !== ACTION_TARGETS.cta || !post.cta) return;
    closePopup();
    navigate(post.cta.route);
  };
  const close = (post: Post, method: CloseMethod, at: TourPosition) => {
    analytics.announcementDismissed({ post_id: post.id, surface, method, page: at.page + 1, step: at.step + 1 });
    closePopup();
  };
  const seeAll = () => {
    closePopup();
    openWhatsNew(PANEL_SOURCES.popupLink);
  };

  return (
    <>
      <SpotlightDialog posts={posts} onPageShown={pageShown} onAction={act} onClose={close} onSeeAll={seeAll} />
      {/* The panel lives here, not in the header, so it still opens while the tour replaces the header. */}
      <WhatsNewDrawer />
      {/* Hints wait for the same clear page as the popup, and give way to a popup that is due. */}
      <PageHint blocked={!pageIsClear || duePages.length > 0} />
    </>
  );
};

export default AnnouncementHost;
